import { isAvailableNow } from '@entities/available-periods';
import { useTz } from '@entities/schedules';
import {
  doCalc15MinutesReminder,
  doRenderEmptySession,
  type DoRenderEmptySessionType,
} from '@entities/schedules/schedule/helpers.tsx';
import { getScheduleServiceDuration } from '@entities/schedules/service/helpers.ts';
import cn from 'classnames';
import type React from 'react';
import { memo } from 'react';
import { AvailableEditorIcon } from '@features/shedules/index.ts';
import { getSessionBlock } from '@shared/helpers';
import type { AvailablePeriod } from '@shared/types/available-periods';
import { StartTime } from '../start-time';
import * as st from './styles.module.scss';

const calculatePeriodId = (blockType: string, availableStart: string) => {
  let output = '';
  blockType.includes('start') && (output = availableStart);
  blockType.includes('single') && (output = availableStart);
  blockType.includes('topOverlap') && (output += `${availableStart}_topOverlap`);
  return output;
};
type SessionType = { start: Date; end: Date };

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  session: SessionType;
  availablePeriod: AvailablePeriod;
  isActive?: boolean;
  hasOneMorePeriod?: boolean;
  emptySessionHandlers?: DoRenderEmptySessionType;
}

export const AvailableEditorSession = memo(
  ({
    session,
    availablePeriod,
    emptySessionHandlers,
    isActive,
    hasOneMorePeriod,
    children,
    ...props
  }: Props) => {
    // const { availablePeriod } = useAvailablePeriod(availableId);
    const { start, end } = session;
    const { tz } = useTz();
    const isNow = isAvailableNow(availablePeriod);

    const startReminder = doCalc15MinutesReminder(new Date(availablePeriod?.start_time));

    const endReminder = doCalc15MinutesReminder(new Date(availablePeriod?.end_time));
    const duration = getScheduleServiceDuration(availablePeriod?.terms[0].services) || 0;

    const blockType = getSessionBlock(
      { start, end },
      tz,
      availablePeriod?.start_time,
      availablePeriod?.end_time
    );

    const id = calculatePeriodId(blockType, availablePeriod?.start_time);

    const showChildren =
      blockType === 'start' ||
      blockType === 'start_bottomOverlap' ||
      blockType === 'single' ||
      blockType === 'single_bottomOverlap';

    return (
      <>
        <div
          className={cn(
            st.availableSession,
            st[blockType],
            isNow && st.now,
            isActive && st.active,
            startReminder === 5 && st.min5,
            startReminder === 10 && st.min10,
            endReminder === 5 && st.end5,
            endReminder === 10 && st.end10,
            endReminder - startReminder === 0 && st.endDiffZero,
            endReminder - startReminder === -5 && st.endDiffMinus5,
            endReminder - startReminder === -10 && st.endDiffMinus10,
            endReminder - startReminder === 5 && st.endDiff5,
            endReminder - startReminder === 10 && st.endDiff10,
            duration > 30 && st.durationBiggerThen30
          )}
          id={id}
          {...props}
        >
          {showChildren && (
            <>
              <StartTime start={availablePeriod?.start_time} />
              <AvailableEditorIcon
                status={availablePeriod?.terms[0]?.status}
                selectedAddressId={availablePeriod?.terms[0]?.address?.id}
                availableId={availablePeriod.id}
              />
            </>
          )}
        </div>
        {blockType.includes('end') &&
          !hasOneMorePeriod &&
          emptySessionHandlers &&
          doRenderEmptySession(emptySessionHandlers)}
      </>
    );
  }
);
